import React, { useState, useEffect, ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { healthService, HealthProgramGroupCalendarDto, setCurrentUser } from '../../services/health';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import Alert from '../../components/UI/Alert';
import { HealthProgramGroupDto } from '../../services/health/types';

const ProgramGroupCalendarEdit: React.FC = () => {
  const { id, calendarId } = useParams<{ id: string, calendarId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);

  const [formData, setFormData] = useState<HealthProgramGroupCalendarDto>({
    name: '',
    description: '',
    timezone: 'AMERICA_SAO_PAULO',
    starts_in: '',
    ends_in: ''
  });

  const [programGroup, setProgramGroup] = useState<HealthProgramGroupDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchCalendar = async () => {
      if (!id || !calendarId) return;
      
      setLoading(true);
      try {
        // Since there's no direct API to get a single calendar, we fetch all calendars and find the one we need
        const response = await healthService.getProgramGroupCalendars(id);
        const calendar = response.content?.find(cal => cal.id === calendarId);
        
        if (calendar) {
          setFormData({
            id: calendar.id,
            name: calendar.name,
            description: calendar.description,
            timezone: calendar.timezone,
            starts_in: calendar.starts_in || '',
            ends_in: calendar.ends_in || '',
            health_program_group_id: id
          });
        } else {
          setError('Calendar not found');
        }
      } catch (error) {
        console.error('Error fetching calendar:', error);
        setError('Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCalendar();
  }, [id, calendarId]);

  useEffect(() => {
    const fetchProgramGroup = async () => {
      if (!id) return;
      
      try {
        const programGroupData = await healthService.getProgramGroupById(id);
        setProgramGroup(programGroupData);
      } catch (error) {
        console.error('Error fetching program group:', error);
      }
    };
    
    fetchProgramGroup();
  }, [id]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is updated
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (!formData.timezone) {
      errors.timezone = 'Timezone is required';
    }

    if (formData.starts_in && formData.ends_in) {
      const startDate = new Date(formData.starts_in);
      const endDate = new Date(formData.ends_in);
      
      if (startDate > endDate) {
        errors.ends_in = 'End date must be after start date';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!id || !calendarId) {
      setError('Program group ID or calendar ID is missing');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      await healthService.updateProgramGroupCalendar(id, calendarId, formData);
      navigate(`/health/program-groups/${id}/calendars`);
    } catch (error) {
      console.error('Error updating calendar:', error);
      setError('Failed to update calendar. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !validationErrors.length) {
    return (
      <div>
        <PageHeader
          title="Edit Calendar"
          breadcrumbs={[
            { label: "Dashboard", to: "/" },
            { label: "Health" },
            { label: "Program Groups", to: "/health/program-groups" },
            { label: programGroup?.name || 'Program Group', to: `/health/program-groups/${id}` },
            { label: "Calendars", to: `/health/program-groups/${id}/calendars` },
            { label: "Edit" }
          ]}
          actions={
            <Button
              variant="secondary"
              onClick={() => navigate(`/health/program-groups/${id}`)}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Program Group
            </Button>
          }
        />
        
        <Card className="mt-6">
          <div className="p-6 text-center">
            <div className="text-red-500 mb-4">{error}</div>
            <Button
              variant="primary"
              onClick={() => navigate(`/health/program-groups/${id}/calendars`)}
            >
              Back to Calendars
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Edit Calendar"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups", to: "/health/program-groups" },
          { label: programGroup?.name || 'Program Group', to: `/health/program-groups/${id}` },
          { label: "Calendars", to: `/health/program-groups/${id}/calendars` },
          { label: "Edit" }
        ]}
        actions={
          <div className="flex space-x-3">
            <Button 
              variant="secondary" 
              onClick={() => navigate(`/health/program-groups/${id}/calendars`)}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Calendars
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/health/program-groups/${id}`)}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Program Group
            </Button>
          </div>
        }
      />

      {error && (
        <div className="mb-4">
          <Alert
            variant="error"
            dismissible
            onDismiss={() => setError(null)}
          >
            {error}
          </Alert>
        </div>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <FormInput
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={validationErrors.name}
            required
          />

          <FormInput
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            error={validationErrors.description}
            required
          />

          <FormSelect
            label="Timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleInputChange}
            error={validationErrors.timezone}
            required
            options={[
              { value: "AMERICA_SAO_PAULO", label: "America/Sao Paulo" },
              { value: "UTC", label: "UTC" }
            ]}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Start Date"
              name="starts_in"
              type="datetime-local"
              value={formData.starts_in ? formData.starts_in.substring(0, 16) : ''}
              onChange={handleInputChange}
              error={validationErrors.starts_in}
            />

            <FormInput
              label="End Date"
              name="ends_in"
              type="datetime-local"
              value={formData.ends_in ? formData.ends_in.substring(0, 16) : ''}
              onChange={handleInputChange}
              error={validationErrors.ends_in}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="secondary"
              onClick={() => navigate(`/health/program-groups/${id}/calendars`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProgramGroupCalendarEdit;